/**
 * Enmon brand theme — dark.
 *
 * Plain data, owned here. Paste a fresh Theme Buddy export straight into the object below and let
 * `satisfies ThemeConfig` do the reviewing: antd only accepts a subset of what Theme Buddy emits, so
 * anything it refuses lights up red in the editor and is deleted on the spot.
 *
 * Two things worth knowing before deleting a flagged key (both hit us on the first import):
 * - antd derives a lot of tokens internally and refuses them as overrides. Those values simply cannot
 *   be set; they follow from the tokens they are derived from.
 * - Theme Buddy sometimes files a token under the wrong component. `groupBorderColor` is an Avatar
 *   token, `handleFontSize` an InputNumber one. Move it rather than drop it.
 */
import type { ThemeConfig } from 'antd';

export const darkTheme = {
  token: {
    colorText: 'rgba(255, 255, 255, 0.85)',
    colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
    colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
    colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',
    colorTextLightSolid: '#ffffff',
    colorTextHeading: 'rgba(255, 255, 255, 0.85)',
    colorTextLabel: 'rgba(255, 255, 255, 0.65)',
    colorTextDescription: 'rgba(255, 255, 255, 0.45)',
    colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
    colorTextPlaceholder: 'rgba(255, 255, 255, 0.25)',
    colorIcon: 'rgba(255, 255, 255, 0.45)',
    colorIconHover: 'rgba(255, 255, 255, 0.85)',
    colorBgContainer: '#131314',
    colorBgElevated: '#1d1e1f',
    colorBgLayout: '#000000',
    colorBgMask: 'rgba(12, 12, 13, 0.45)',
    colorBgSpotlight: '#3f4042',
    colorBorder: '#3f4042',
    colorBorderSecondary: '#2e2e30',
    colorFill: 'rgba(255, 255, 255, 0.18)',
    colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
    colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
    colorFillQuaternary: 'rgba(255, 255, 255, 0.04)',
    colorWhite: '#ffffff',
    colorBgBase: '#000000',
    colorTextBase: '#ffffff',
    colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
    colorBgTextActive: 'rgba(255, 255, 255, 0.18)',
    colorBgTextHover: 'rgba(255, 255, 255, 0.12)',
    colorBorderBg: '#131314',
    colorFillContent: 'rgba(255, 255, 255, 0.12)',
    colorFillContentHover: 'rgba(255, 255, 255, 0.18)',
    colorFillAlter: 'rgba(255, 255, 255, 0.04)',
    colorSplit: 'rgba(255, 255, 255, 0.06)',
    colorPrimary: '#13a8a1',
    colorSuccess: '#69aa19',
    colorWarning: '#d87716',
    colorInfo: '#13a8a1',
    colorError: '#e84747',
    colorLink: '#13a8a1',
    colorErrorBg: '#2a1212',
    colorErrorBgHover: '#431414',
    colorErrorBorder: '#581818',
    colorErrorBorderHover: '#791a1a',
    colorErrorHover: '#791a1a',
    colorErrorActive: '#f37070',
    colorErrorTextHover: '#e84747',
    colorErrorText: '#d32020',
    colorErrorTextActive: '#f37070',
    colorLinkHover: '#33bcb5',
    colorInfoBg: '#112322',
    colorInfoBgHover: '#113634',
    colorInfoBorder: '#144845',
    colorInfoBorderHover: '#33bcb5',
    colorInfoHover: '#33bcb5',
    colorInfoActive: '#13857f',
    colorInfoTextHover: '#33bcb5',
    colorInfoText: '#13a8a1',
    colorInfoTextActive: '#13857f',
    colorLinkActive: '#13857f',
    colorPrimaryBg: '#112322',
    colorPrimaryBgHover: '#113634',
    colorPrimaryBorder: '#144845',
    colorPrimaryBorderHover: '#14625e',
    colorPrimaryHover: '#33bcb5',
    colorPrimaryActive: '#13857f',
    colorPrimaryTextHover: '#33bcb5',
    colorPrimaryText: '#13a8a1',
    colorPrimaryTextActive: '#13857f',
    colorSuccessBg: '#1b2312',
    colorSuccessBgHover: '#263712',
    colorSuccessBorder: '#324916',
    colorSuccessBorderHover: '#416317',
    colorSuccessHover: '#416317',
    colorSuccessActive: '#558618',
    colorSuccessTextHover: '#82be39',
    colorSuccessText: '#69aa19',
    colorSuccessTextActive: '#558618',
    colorWarningBg: '#2b1e11',
    colorWarningBgHover: '#442b11',
    colorWarningBorder: '#593715',
    colorWarningBorderHover: '#7c4915',
    colorWarningHover: '#7c4915',
    colorWarningActive: '#aa6015',
    colorWarningTextHover: '#e8923c',
    colorWarningText: '#d87716',
    colorWarningTextActive: '#aa6015',
    colorErrorOutline: 'rgba(238, 38, 38, 0.11)',
    colorWarningOutline: 'rgba(173, 87, 0, 0.15)',
    controlItemBgActive: '#112322',
    controlItemBgActiveDisabled: 'rgba(255, 255, 255, 0.18)',
    controlItemBgActiveHover: '#113634',
    controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
    controlOutline: 'rgba(64, 217, 255, 0.15)',
    controlTmpOutline: 'rgba(255, 255, 255, 0.04)',
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    borderRadiusXS: 2,
    sizeStep: 4,
    sizeUnit: 4,
    controlInteractiveSize: 16,
    size: 16,
    sizeLG: 24,
    sizeMD: 20,
    sizeMS: 16,
    sizeSM: 12,
    sizeXL: 32,
    sizeXS: 8,
    sizeXXL: 48,
    controlHeight: 32,
    sizeXXS: 4,
    controlHeightLG: 40,
    controlHeightSM: 24,
    controlHeightXS: 16,
    lineWidth: 1,
    lineWidthBold: 2,
    lineWidthFocus: 4,
    controlOutlineWidth: 2,
    screenLG: 992,
    screenLGMax: 1199,
    screenLGMin: 992,
    screenMD: 768,
    screenMDMax: 991,
    screenMDMin: 768,
    screenSM: 576,
    screenSMMax: 767,
    screenSMMin: 576,
    screenXL: 1200,
    screenXLMax: 1599,
    screenXLMin: 1200,
    screenXS: 480,
    screenXSMax: 575,
    screenXSMin: 480,
    screenXXL: 1600,
    screenXXLMin: 1600,
    sizePopupArrow: 16,
    margin: 16,
    marginLG: 24,
    marginMD: 20,
    marginSM: 12,
    marginXL: 32,
    marginXS: 8,
    marginXXL: 48,
    marginXXS: 4,
    padding: 16,
    paddingLG: 24,
    paddingMD: 20,
    paddingSM: 12,
    paddingXL: 32,
    paddingXS: 8,
    paddingXXS: 4,
    paddingContentHorizontal: 16,
    paddingContentHorizontalLG: 24,
    paddingContentHorizontalSM: 16,
    paddingContentVertical: 12,
    paddingContentVerticalLG: 16,
    paddingContentVerticalSM: 8,
    controlPaddingHorizontal: 12,
    controlPaddingHorizontalSM: 8,
    fontFamily: 'Poppins',
    fontFamilyCode: 'Roboto Mono',
    fontSize: 14,
    fontSizeLG: 16,
    fontSizeSM: 12,
    fontSizeXL: 20,
    fontSizeHeading1: 38,
    fontSizeHeading2: 30,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,
    lineHeight: 1.5714285714285714,
    lineHeightHeading1: 1.2105263157894737,
    lineHeightHeading2: 1.2666666666666666,
    lineHeightHeading3: 1.3333333333333333,
    lineHeightHeading4: 1.4,
    lineHeightHeading5: 1.5,
    lineHeightLG: 1.5,
    lineHeightSM: 1.6666666666666667,
    fontSizeIcon: 12,
    fontWeightStrong: 600,
    colorBgSolid: 'rgba(255, 255, 255, 0.95)',
    colorBgSolidActive: 'rgba(255, 255, 255, 0.9)',
    colorBgSolidHover: '#ffffff',
  },
  components: {
    Input: {
      paddingInlineSM: 7,
      paddingInlineLG: 11,
      paddingInline: 11,
      paddingBlockSM: 0,
      paddingBlockLG: 7,
      paddingBlock: 4,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.15)',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWarningOutline: 'rgba(173, 87, 0, 0.15)',
      colorWarningBorderHover: '#7c4915',
      colorWarning: '#d87716',
      colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
      colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.25)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimary: '#13a8a1',
      colorIconHover: 'rgba(255, 255, 255, 0.85)',
      colorIcon: 'rgba(255, 255, 255, 0.45)',
      colorFillAlter: 'rgba(255, 255, 255, 0.04)',
      colorErrorOutline: 'rgba(238, 38, 38, 0.11)',
      colorErrorBorderHover: '#791a1a',
      colorError: '#e84747',
      colorBorder: '#3f4042',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      hoverBorderColor: '#33bcb5',
      addonBg: 'rgba(255, 255, 255, 0.04)',
      activeBorderColor: '#13a8a1',
      colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
      colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
      colorErrorBgHover: '#431414',
      colorErrorBg: '#2a1212',
      colorWarningBg: '#2b1e11',
      colorWarningBgHover: '#442b11',
      colorWarningText: '#d87716',
      colorErrorText: '#d32020',
      activeBg: '#131314',
      hoverBg: '#131314',
      inputFontSize: 14,
      inputFontSizeLG: 16,
      inputFontSizeSM: 12,
      fontFamily: 'Poppins',
    },
    Transfer: {
      listWidthLG: 250,
      listWidth: 180,
      listHeight: 200,
      itemPaddingBlock: 5,
      paddingXS: 8,
      paddingSM: 12,
      marginXXS: 4,
      marginXS: 8,
      margin: 16,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeIcon: 12,
      fontSize: 14,
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlItemBgActiveHover: '#113634',
      controlItemBgActive: '#112322',
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusLG: 8,
      colorWarning: '#d87716',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorLinkHover: '#33bcb5',
      colorError: '#e84747',
      colorBorder: '#3f4042',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      itemHeight: 32,
      headerHeight: 40,
      fontFamily: 'Poppins',
    },
    Segmented: {
      paddingXXS: 4,
      marginSM: 12,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorTextLabel: 'rgba(255, 255, 255, 0.65)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
      colorFill: 'rgba(255, 255, 255, 0.18)',
      colorBgLayout: '#000000',
      colorBgElevated: '#1d1e1f',
      itemSelectedColor: 'rgba(255, 255, 255, 0.85)',
      itemSelectedBg: '#1d1e1f',
      itemHoverColor: 'rgba(255, 255, 255, 0.85)',
      itemHoverBg: 'rgba(255, 255, 255, 0.12)',
      itemColor: 'rgba(255, 255, 255, 0.65)',
      itemActiveBg: 'rgba(255, 255, 255, 0.18)',
      trackPadding: 2,
      trackBg: '#000000',
      fontFamily: 'Poppins',
    },
    Switch: {
      trackPadding: 2,
      trackMinWidthSM: 28,
      trackMinWidth: 44,
      trackHeightSM: 16,
      trackHeight: 22,
      handleSizeSM: 12,
      handleSize: 18,
      marginXXS: 4,
      lineWidthFocus: 4,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeIcon: 12,
      fontSize: 14,
      controlHeight: 32,
      colorWhite: '#ffffff',
      colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
      colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      handleBg: '#ffffff',
      fontFamily: 'Poppins',
      innerMaxMargin: 24,
      innerMaxMarginSM: 18,
      innerMinMargin: 9,
      innerMinMarginSM: 6,
    },
    Timeline: {
      itemPaddingBottom: 20,
      paddingXXS: 4,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      marginSM: 12,
      margin: 16,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSize: 14,
      controlHeightLG: 40,
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSuccess: '#69aa19',
      colorPrimary: '#13a8a1',
      colorError: '#e84747',
      colorBgContainer: '#131314',
      tailColor: 'rgba(255, 255, 255, 0.06)',
      dotBg: '#131314',
      fontFamily: 'Poppins',
      tailWidth: 2,
      paddingSM: 12,
    },
    Tabs: {
      horizontalItemGutter: 32,
      cardGutter: 2,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      marginSM: 12,
      margin: 16,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSize: 14,
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimaryBorder: '#144845',
      colorPrimaryActive: '#13857f',
      colorPrimary: '#13a8a1',
      colorFillAlter: 'rgba(255, 255, 255, 0.04)',
      colorBorderSecondary: '#2e2e30',
      colorBorder: '#3f4042',
      colorBgContainer: '#131314',
      itemSelectedColor: '#13a8a1',
      itemHoverColor: '#33bcb5',
      itemColor: 'rgba(255, 255, 255, 0.85)',
      itemActiveColor: '#13857f',
      inkBarColor: '#13a8a1',
      cardHeight: 40,
      cardBg: 'rgba(255, 255, 255, 0.04)',
      titleFontSize: 14,
      titleFontSizeLG: 16,
      titleFontSizeSM: 14,
      fontFamily: 'Poppins',
      cardHeightLG: 48,
    },
    Table: {
      stickyScrollBarBorderRadius: 100,
      headerSplitColor: '#2e2e30',
      headerBg: '#1c1c1d',
      footerBg: '#1c1c1d',
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      padding: 16,
      marginXXS: 4,
      margin: 16,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 500,
      fontSizeSM: 12,
      fontSizeIcon: 12,
      fontSize: 14,
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlItemBgActiveHover: '#113634',
      controlItemBgActive: '#112322',
      controlInteractiveSize: 16,
      controlHeight: 32,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.25)',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorPrimary: '#13a8a1',
      colorLinkHover: '#33bcb5',
      colorLinkActive: '#13857f',
      colorLink: '#13a8a1',
      colorIconHover: 'rgba(255, 255, 255, 0.85)',
      colorIcon: 'rgba(255, 255, 255, 0.45)',
      colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
      colorFillContent: 'rgba(255, 255, 255, 0.12)',
      colorFillAlter: 'rgba(255, 255, 255, 0.04)',
      colorBorderSecondary: '#2e2e30',
      colorBgContainer: '#131314',
      rowSelectedHoverBg: '#113634',
      headerFilterHoverBg: 'rgba(255, 255, 255, 0.12)',
      headerColor: 'rgba(255, 255, 255, 0.85)',
      headerBorderRadius: 8,
      footerColor: 'rgba(255, 255, 255, 0.85)',
      filterDropdownMenuBg: '#131314',
      filterDropdownBg: '#131314',
      cellPaddingInlineSM: 8,
      cellPaddingInlineMD: 8,
      cellPaddingInline: 16,
      cellPaddingBlockSM: 8,
      cellPaddingBlockMD: 12,
      cellPaddingBlock: 16,
      borderColor: '#2e2e30',
      rowHoverBg: '#1d1d1d',
      headerSortActiveBg: '#373738',
      bodySortBg: '#1d1d1d',
      headerSortHoverBg: '#373738',
      cellFontSize: 14,
      cellFontSizeMD: 14,
      cellFontSizeSM: 14,
      fontFamily: 'Poppins',
      expandIconBg: '#131314',
      rowExpandedBg: '#1c1c1d',
      rowSelectedBg: '#112322',
      selectionColumnWidth: 32,
      stickyScrollBarBg: 'rgba(255, 255, 255, 0.25)',
    },
    Steps: {
      dotSize: 8,
      dotCurrentSize: 10,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      marginSM: 12,
      marginLG: 24,
      margin: 16,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeightSM: 1.6666666666666667,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSizeHeading3: 24,
      fontSize: 14,
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlItemBgActive: '#112322',
      controlHeightLG: 40,
      borderRadiusSM: 4,
      colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',
      colorTextLightSolid: '#ffffff',
      colorTextLabel: 'rgba(255, 255, 255, 0.65)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      colorFillContent: 'rgba(255, 255, 255, 0.12)',
      colorError: '#e84747',
      colorBorderSecondary: '#2e2e30',
      colorBorderBg: '#131314',
      colorBgContainer: '#131314',
      titleLineHeight: 32,
      iconSizeSM: 24,
      iconSize: 32,
      customIconFontSize: 24,
      iconFontSize: 14,
      fontFamily: 'Poppins',
      navArrowColor: 'rgba(255, 255, 255, 0.25)',
      borderRadius: 6,
      colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
      colorPrimaryBg: '#112322',
      colorErrorBg: '#2a1212',
      colorPrimaryHover: '#33bcb5',
      colorErrorHover: '#791a1a',
    },
    Spin: {
      dotSizeSM: 14,
      dotSize: 20,
      contentHeight: 400,
      marginXXS: 4,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeightLG: 40,
      controlHeight: 32,
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimary: '#13a8a1',
      colorBgContainer: '#131314',
      dotSizeLG: 32,
      fontFamily: 'Poppins',
    },
    Slider: {
      railSize: 4,
      handleSizeHover: 12,
      dotSize: 8,
      controlSize: 10,
      handleSize: 10,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusXS: 2,
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryBorderHover: '#14625e',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
      colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
      colorFillContentHover: 'rgba(255, 255, 255, 0.18)',
      colorBorderSecondary: '#2e2e30',
      colorBgElevated: '#1d1e1f',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      trackHoverBg: '#14625e',
      trackBgDisabled: 'rgba(255, 255, 255, 0.08)',
      trackBg: '#144845',
      railHoverBg: 'rgba(255, 255, 255, 0.12)',
      railBg: 'rgba(255, 255, 255, 0.08)',
      handleColor: '#144845',
      handleActiveColor: '#13a8a1',
      dotBorderColor: '#2e2e30',
      dotActiveBorderColor: '#144845',
      handleColorDisabled: '#bfbfbf',
      fontFamily: 'Poppins',
      handleActiveOutlineColor: 'rgba(22, 119, 255, 0.2)',
      handleLineWidth: 2,
      handleLineWidthHover: 2.5,
    },
    Rate: {
      marginXS: 8,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeightLG: 40,
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorFillContent: 'rgba(255, 255, 255, 0.12)',
      fontFamily: 'Poppins',
      starColor: '#d8c414',
      starBg: 'rgba(255, 255, 255, 0.12)',
      starSize: 20,
      starSizeLG: 25,
      starSizeSM: 15,
    },
    Radio: {
      radioSize: 16,
      dotSize: 8,
      buttonPaddingInline: 15,
      paddingXS: 8,
      padding: 16,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.15)',
      controlItemBgActiveDisabled: 'rgba(255, 255, 255, 0.18)',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWhite: '#ffffff',
      colorTextLightSolid: '#ffffff',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimaryBorder: '#144845',
      colorPrimaryActive: '#13857f',
      colorPrimary: '#13a8a1',
      colorBorder: '#3f4042',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      wrapperMarginInlineEnd: 8,
      dotColorDisabled: 'rgba(255, 255, 255, 0.25)',
      buttonSolidCheckedHoverBg: '#33bcb5',
      buttonSolidCheckedColor: '#ffffff',
      buttonSolidCheckedBg: '#13a8a1',
      buttonSolidCheckedActiveBg: '#13857f',
      buttonColor: 'rgba(255, 255, 255, 0.85)',
      buttonCheckedColorDisabled: 'rgba(255, 255, 255, 0.25)',
      buttonCheckedBgDisabled: 'rgba(255, 255, 255, 0.18)',
      buttonCheckedBg: '#131314',
      buttonBg: '#131314',
      fontFamily: 'Poppins',
    },
    Popover: {
      titleMinWidth: 177,
      sizePopupArrow: 16,
      paddingSM: 12,
      padding: 16,
      marginXS: 8,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSize: 14,
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadiusLG: 8,
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorBgElevated: '#1d1e1f',
      fontFamily: 'Poppins',
    },
    Notification: {
      width: 384,
      paddingMD: 20,
      paddingLG: 24,
      paddingContentHorizontalLG: 24,
      marginXS: 8,
      marginSM: 12,
      marginLG: 24,
      margin: 16,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlHeightLG: 40,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      colorWarning: '#d87716',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSuccess: '#69aa19',
      colorInfo: '#13a8a1',
      colorIconHover: 'rgba(255, 255, 255, 0.85)',
      colorIcon: 'rgba(255, 255, 255, 0.45)',
      colorError: '#e84747',
      colorBgElevated: '#1d1e1f',
      lineHeightLG: 1.5,
      lineWidthFocus: 4,
      fontFamily: 'Poppins',
    },
    Tooltip: {
      paddingSM: 12,
      sizePopupArrow: 16,
      paddingXS: 8,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadius: 6,
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorBgSpotlight: '#3f4042',
      fontFamily: 'Poppins',
      maxWidth: 250,
    },
    Menu: {
      subMenuItemBg: 'rgba(255, 255, 255, 0.04)',
      darkSubMenuItemBg: '#000c17',
      darkItemDisabledColor: 'rgba(255, 255, 255, 0.25)',
      darkItemColor: 'rgba(255, 255, 255, 0.65)',
      darkGroupTitleColor: 'rgba(255, 255, 255, 0.65)',
      darkItemBg: '#001529',
      paddingXS: 8,
      paddingXL: 32,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      margin: 16,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlItemBgActive: '#112322',
      controlHeightSM: 24,
      controlHeightLG: 40,
      borderRadius: 6,
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      colorFillContent: 'rgba(255, 255, 255, 0.12)',
      colorErrorHover: '#791a1a',
      colorErrorBg: '#2a1212',
      colorError: '#e84747',
      colorBgElevated: '#1d1e1f',
      colorBgContainer: '#131314',
      subMenuItemBorderRadius: 4,
      popupBg: '#1d1e1f',
      itemSelectedColor: '#13a8a1',
      itemSelectedBg: '#112322',
      itemHoverColor: 'rgba(255, 255, 255, 0.85)',
      itemHoverBg: 'rgba(255, 255, 255, 0.12)',
      itemHeight: 40,
      itemDisabledColor: 'rgba(255, 255, 255, 0.25)',
      itemColor: 'rgba(255, 255, 255, 0.85)',
      itemBorderRadius: 8,
      itemBg: '#131314',
      iconSize: 16,
      horizontalItemSelectedColor: '#13a8a1',
      horizontalItemSelectedBg: 'rgba(255, 255, 255, 0)',
      horizontalItemHoverColor: '#13a8a1',
      horizontalItemHoverBg: 'rgba(255, 255, 255, 0)',
      groupTitleColor: 'rgba(255, 255, 255, 0.45)',
      darkItemSelectedColor: '#ffffff',
      darkItemSelectedBg: '#13a8a1',
      darkItemHoverColor: '#ffffff',
      darkItemHoverBg: 'rgba(255, 255, 255, 0)',
      collapsedIconSize: 16,
      darkPopupBg: '#001529',
      activeBarBorderWidth: 1,
      collapsedWidth: 80,
      dropdownWidth: 160,
      itemMarginBlock: 4,
      itemMarginInline: 4,
      iconMarginInlineEnd: 10,
      groupTitleFontSize: 14,
      groupTitleLineHeight: 1.5714285714285714,
      fontFamily: 'Poppins',
      subMenuItemSelectedColor: '#13a8a1',
      itemPaddingInline: 16,
      horizontalLineHeight: 46,
      itemActiveBg: '#112322',
      dangerItemActiveBg: '#2a1212',
      dangerItemColor: '#e84747',
      dangerItemHoverColor: '#e84747',
      dangerItemSelectedBg: '#2a1212',
      dangerItemSelectedColor: '#e84747',
      darkDangerItemActiveBg: '#e84747',
      darkDangerItemColor: '#e84747',
      darkDangerItemHoverColor: '#791a1a',
      darkDangerItemSelectedBg: '#e84747',
      darkDangerItemSelectedColor: '#ffffff',
    },
    InputNumber: {
      paddingInlineSM: 7,
      paddingInlineLG: 11,
      paddingInline: 11,
      paddingBlockSM: 0,
      paddingBlockLG: 7,
      paddingBlock: 4,
      handleWidth: 22,
      controlWidth: 90,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWarningBorderHover: '#7c4915',
      colorWarning: '#d87716',
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.25)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimary: '#13a8a1',
      colorFillAlter: 'rgba(255, 255, 255, 0.04)',
      colorErrorBorderHover: '#791a1a',
      colorError: '#e84747',
      colorBorder: '#3f4042',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      hoverBorderColor: '#33bcb5',
      handleHoverColor: '#13a8a1',
      handleBorderColor: '#3f4042',
      handleBg: '#131314',
      handleActiveBg: 'rgba(255, 255, 255, 0.04)',
      addonBg: 'rgba(255, 255, 255, 0.04)',
      activeBorderColor: '#13a8a1',
      activeBg: '#131314',
      hoverBg: '#131314',
      inputFontSize: 14,
      inputFontSizeLG: 16,
      inputFontSizeSM: 12,
      fontFamily: 'Poppins',
      colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',
      colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
      colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
      colorWarningBg: '#2b1e11',
      colorWarningBgHover: '#442b11',
      colorErrorBg: '#2a1212',
      colorErrorBgHover: '#431414',
      filledHandleBg: '#303030',
      colorErrorText: '#d32020',
      colorWarningText: '#d87716',
    },
    Image: {
      previewOperationSize: 18,
      previewOperationHoverColor: 'rgba(255, 255, 255, 0.85)',
      previewOperationColorDisabled: 'rgba(255, 255, 255, 0.25)',
      previewOperationColor: 'rgba(255, 255, 255, 0.65)',
      paddingXXS: 4,
      paddingSM: 12,
      paddingLG: 24,
      marginXXS: 4,
      marginXL: 32,
      marginSM: 12,
      margin: 16,
      fontSizeIcon: 12,
      controlHeightLG: 40,
      colorTextLightSolid: '#ffffff',
      colorBgMask: 'rgba(12, 12, 13, 0.45)',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
    },
    Card: {
      headerHeightSM: 38,
      headerHeight: 56,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSize: 14,
      borderRadiusLG: 8,
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimary: '#13a8a1',
      colorFillAlter: 'rgba(255, 255, 255, 0.04)',
      colorBorderSecondary: '#2e2e30',
      colorBgContainer: '#131314',
      headerBg: 'rgba(255, 255, 255, 0)',
      headerFontSize: 16,
      headerFontSizeSM: 14,
      fontSizeLG: 16,
      fontFamily: 'Poppins',
      bodyPaddingSM: 12,
      headerPaddingSM: 12,
      bodyPadding: 24,
      headerPadding: 24,
      actionsBg: '#131314',
    },
    Carousel: {
      dotWidth: 16,
      dotHeight: 3,
      dotActiveWidth: 24,
      marginXXS: 4,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeightSM: 24,
      controlHeightLG: 40,
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorBgContainer: '#131314',
      fontFamily: 'Poppins',
      arrowOffset: 8,
      arrowSize: 16,
      dotGap: 4,
      dotOffset: 12,
    },
    Cascader: {
      dropdownHeight: 180,
      controlWidth: 184,
      controlItemWidth: 111,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSize: 14,
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlItemBgActive: '#112322',
      controlInteractiveSize: 16,
      controlHeight: 32,
      borderRadiusSM: 4,
      colorWhite: '#ffffff',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorPrimaryHover: '#33bcb5',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      colorBorder: '#3f4042',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      optionSelectedBg: '#112322',
      menuPadding: 4,
      optionSelectedFontWeight: 600,
      fontFamily: 'Poppins',
      borderRadiusLG: 8,
      optionSelectedColor: 'rgba(255, 255, 255, 0.85)',
    },
    Calendar: {
      yearControlWidth: 80,
      monthControlWidth: 70,
      controlHeight: 32,
      screenXS: 480,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeightSM: 1.6666666666666667,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeSM: 12,
      fontSize: 14,
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlItemBgActive: '#112322',
      controlHeightSM: 24,
      controlHeightLG: 40,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      colorTextLightSolid: '#ffffff',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorPrimary: '#13a8a1',
      colorLinkHover: '#33bcb5',
      colorLinkActive: '#13857f',
      colorLink: '#13a8a1',
      colorIconHover: 'rgba(255, 255, 255, 0.85)',
      colorIcon: 'rgba(255, 255, 255, 0.45)',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      itemActiveBg: '#112322',
      fullPanelBg: '#131314',
      fullBg: '#131314',
      fontFamily: 'Poppins',
      miniContentHeight: 256,
    },
    Button: {
      paddingInlineSM: 7,
      paddingInlineLG: 15,
      paddingInline: 15,
      onlyIconSizeSM: 14,
      onlyIconSizeLG: 18,
      paddingXS: 8,
      paddingContentHorizontal: 16,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidth: 1,
      contentLineHeight: 1.5714285714285714,
      contentFontSizeLG: 16,
      contentFontSize: 14,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.15)',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorTextLightSolid: '#ffffff',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimaryBorder: '#144845',
      colorPrimaryActive: '#13857f',
      colorPrimary: '#13a8a1',
      colorLinkHover: '#33bcb5',
      colorLinkActive: '#13857f',
      colorLink: '#13a8a1',
      colorErrorOutline: 'rgba(238, 38, 38, 0.11)',
      colorErrorHover: '#791a1a',
      colorErrorBorderHover: '#791a1a',
      colorErrorBg: '#2a1212',
      colorErrorActive: '#f37070',
      colorError: '#e84747',
      colorBorder: '#3f4042',
      colorBgTextActive: 'rgba(255, 255, 255, 0.18)',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      textHoverBg: 'rgba(255, 255, 255, 0.12)',
      primaryColor: '#ffffff',
      onlyIconSize: 16,
      linkHoverBg: 'rgba(255, 255, 255, 0)',
      ghostBg: 'rgba(255, 255, 255, 0)',
      defaultGhostColor: '#131314',
      defaultGhostBorderColor: '#131314',
      defaultColor: 'rgba(255, 255, 255, 0.85)',
      defaultBorderColor: '#3f4042',
      defaultBg: '#131314',
      dangerColor: '#ffffff',
      borderColorDisabled: '#3f4042',
      defaultHoverBg: '#131314',
      defaultHoverColor: '#33bcb5',
      defaultHoverBorderColor: '#33bcb5',
      defaultActiveBg: '#131314',
      defaultActiveColor: '#13857f',
      defaultActiveBorderColor: '#13857f',
      fontWeight: 400,
      contentFontSizeSM: 14,
      contentLineHeightLG: 1.5,
      contentLineHeightSM: 1.5714285714285714,
      textTextActiveColor: 'rgba(255, 255, 255, 0.85)',
      textTextHoverColor: 'rgba(255, 255, 255, 0.85)',
      textTextColor: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryBg: '#112322',
      colorBgSolid: 'rgba(255, 255, 255, 0.95)',
      colorBgSolidActive: 'rgba(255, 255, 255, 0.9)',
      colorBgSolidHover: '#ffffff',
      colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
      solidTextColor: '#0c0c0d',
      dashedBgDisabled: 'rgba(0, 0, 0, 0.04)',
    },
    Badge: {
      statusSize: 6,
      indicatorHeight: 20,
      dotSize: 6,
      paddingXS: 8,
      marginXS: 8,
      lineWidth: 1,
      borderRadiusSM: 4,
      fontSizeSM: 12,
      fontSize: 14,
      colorWarning: '#d87716',
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.25)',
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSuccess: '#69aa19',
      colorPrimary: '#13a8a1',
      colorErrorHover: '#791a1a',
      colorError: '#e84747',
      colorBorderBg: '#131314',
      colorBgContainer: '#131314',
      indicatorHeightSM: 14,
      textFontSize: 12,
      textFontSizeSM: 12,
      lineHeight: 1.5714285714285714,
      fontFamily: 'Poppins',
    },
    Form: {
      screenXSMax: 575,
      screenSMMax: 767,
      screenMDMax: 991,
      screenLGMax: 1199,
      paddingXS: 8,
      paddingSM: 12,
      marginXXS: 4,
      marginXS: 8,
      marginLG: 24,
      margin: 16,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.15)',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      colorWarning: '#d87716',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSuccess: '#69aa19',
      colorPrimary: '#13a8a1',
      colorError: '#e84747',
      colorBorder: '#3f4042',
      labelRequiredMarkColor: '#e84747',
      labelColor: 'rgba(255, 255, 255, 0.85)',
      itemMarginBottom: 24,
      labelColonMarginInlineEnd: 8,
      labelColonMarginInlineStart: 2,
      labelHeight: 32,
      labelFontSize: 14,
      fontFamily: 'Poppins',
    },
    Avatar: {
      marginXXS: 4,
      marginXS: 8,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeXL: 20,
      fontSizeLG: 16,
      fontSizeHeading3: 24,
      fontSize: 14,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.25)',
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorBorderBg: '#131314',
      containerSizeSM: 24,
      containerSizeLG: 40,
      containerSize: 32,
      textFontSize: 18,
      textFontSizeLG: 24,
      textFontSizeSM: 14,
      fontFamily: 'Poppins',
      groupBorderColor: '#131314',
      groupOverlapping: -8,
      iconFontSize: 18,
      iconFontSizeLG: 24,
      iconFontSizeSM: 14,
    },
    Tour: {
      sizePopupArrow: 16,
      paddingXS: 8,
      padding: 16,
      marginXS: 8,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSize: 14,
      borderRadiusXS: 2,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWhite: '#ffffff',
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimary: '#13a8a1',
      colorIconHover: 'rgba(255, 255, 255, 0.85)',
      colorIcon: 'rgba(255, 255, 255, 0.45)',
      colorFill: 'rgba(255, 255, 255, 0.18)',
      colorBgTextHover: 'rgba(255, 255, 255, 0.12)',
      colorBgElevated: '#1d1e1f',
      closeBtnSize: 22,
      primaryNextBtnHoverBg: '#f0f0f0',
      primaryPrevBtnBg: 'rgba(255, 255, 255, 0.15)',
      fontFamily: 'Poppins',
      lineWidthFocus: 4,
    },
    QRCode: {
      paddingSM: 12,
      marginXS: 8,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      controlHeight: 32,
      borderRadiusLG: 8,
      colorWhite: '#ffffff',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      fontFamily: 'Poppins',
    },
    Upload: {
      paddingXS: 8,
      paddingSM: 12,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      marginXL: 32,
      margin: 16,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSizeHeading3: 24,
      fontSizeHeading2: 30,
      fontSize: 14,
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlHeightLG: 40,
      borderRadiusLG: 8,
      colorTextLightSolid: '#ffffff',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimary: '#13a8a1',
      colorFillAlter: 'rgba(255, 255, 255, 0.04)',
      colorErrorBg: '#2a1212',
      colorError: '#e84747',
      colorBorder: '#3f4042',
      colorBgMask: 'rgba(12, 12, 13, 0.45)',
      lineWidthFocus: 4,
      fontFamily: 'Poppins',
      actionsColor: 'rgba(255, 255, 255, 0.45)',
      pictureCardSize: 102,
    },
    Typography: {
      paddingSM: 12,
      marginXXS: 4,
      marginXS: 8,
      lineWidth: 1,
      lineHeightHeading5: 1.5,
      lineHeightHeading4: 1.4,
      lineHeightHeading3: 1.3333333333333333,
      lineHeightHeading2: 1.2666666666666666,
      lineHeightHeading1: 1.2105263157894737,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeHeading5: 16,
      fontSizeHeading4: 20,
      fontSizeHeading3: 24,
      fontSizeHeading2: 30,
      fontSizeHeading1: 38,
      fontSize: 14,
      colorWarning: '#d87716',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSuccess: '#69aa19',
      colorLinkHover: '#33bcb5',
      colorLinkActive: '#13857f',
      colorLink: '#13a8a1',
      colorErrorHover: '#791a1a',
      colorErrorActive: '#f37070',
      colorError: '#e84747',
      fontFamilyCode: 'Roboto Mono',
    },
    TreeSelect: {
      paddingXS: 8,
      marginXXS: 4,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlItemBgActive: '#112322',
      controlInteractiveSize: 16,
      controlHeightSM: 24,
      borderRadiusSM: 4,
      borderRadius: 6,
      colorWhite: '#ffffff',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      colorBorder: '#3f4042',
      colorBgElevated: '#1d1e1f',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      titleHeight: 24,
      nodeSelectedBg: '#112322',
      nodeHoverBg: 'rgba(255, 255, 255, 0.08)',
      fontFamily: 'Poppins',
      borderRadiusLG: 8,
    },
    Tree: {
      paddingSM: 12,
      borderRadiusLG: 8,
      paddingXS: 8,
      marginXXS: 4,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlItemBgActive: '#112322',
      controlInteractiveSize: 16,
      controlHeightSM: 24,
      borderRadiusSM: 4,
      borderRadius: 6,
      colorWhite: '#ffffff',
      colorTextLightSolid: '#ffffff',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      colorBorder: '#3f4042',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      titleHeight: 24,
      nodeSelectedBg: '#112322',
      nodeHoverBg: 'rgba(255, 255, 255, 0.08)',
      directoryNodeSelectedColor: '#ffffff',
      directoryNodeSelectedBg: '#13a8a1',
      fontFamily: 'Poppins',
      nodeHoverColor: 'rgba(255, 255, 255, 0.85)',
      nodeSelectedColor: 'rgba(255, 255, 255, 0.85)',
      indentSize: 24,
    },
    Tag: {
      paddingXXS: 4,
      marginXS: 8,
      lineWidth: 1,
      lineHeightSM: 1.6666666666666667,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeIcon: 12,
      fontSize: 14,
      borderRadiusSM: 4,
      colorTextLightSolid: '#ffffff',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimaryActive: '#13857f',
      colorPrimary: '#13a8a1',
      colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
      colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
      colorFillQuaternary: 'rgba(255, 255, 255, 0.04)',
      colorBorder: '#3f4042',
      colorWarningBorder: '#593715',
      colorWarningBg: '#2b1e11',
      colorSuccessBorder: '#324916',
      colorSuccessBg: '#1b2312',
      colorInfoBorder: '#144845',
      colorInfoBg: '#112322',
      colorErrorBorder: '#581818',
      colorErrorBg: '#2a1212',
      defaultColor: 'rgba(255, 255, 255, 0.85)',
      defaultBg: '#252627',
      fontFamily: 'Poppins',
      solidTextColor: '#0c0c0d',
      colorBgSolid: 'rgba(255, 255, 255, 0.95)',
      colorIcon: 'rgba(255, 255, 255, 0.45)',
    },
    Statistic: {
      padding: 16,
      marginXXS: 4,
      lineHeight: 1.5714285714285714,
      fontSizeHeading3: 24,
      fontSize: 14,
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      contentFontSize: 24,
      titleFontSize: 14,
      fontFamily: 'Poppins',
    },
    Skeleton: {
      padding: 16,
      marginXXS: 4,
      marginSM: 12,
      marginLG: 24,
      controlHeightXS: 16,
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      colorFillContent: 'rgba(255, 255, 255, 0.12)',
      colorFill: 'rgba(255, 255, 255, 0.18)',
      blockRadius: 4,
      paragraphLiHeight: 16,
      paragraphMarginTop: 28,
      titleHeight: 16,
    },
    Select: {
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.15)',
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlItemBgActive: '#112322',
      controlHeightXS: 16,
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWarningOutline: 'rgba(173, 87, 0, 0.15)',
      colorWarningHover: '#7c4915',
      colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
      colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.25)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimary: '#13a8a1',
      colorIconHover: 'rgba(255, 255, 255, 0.85)',
      colorIcon: 'rgba(255, 255, 255, 0.45)',
      colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
      colorErrorOutline: 'rgba(238, 38, 38, 0.11)',
      colorErrorHover: '#791a1a',
      colorBorder: '#3f4042',
      colorBgElevated: '#1d1e1f',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      singleItemHeightLG: 40,
      selectorBg: '#131314',
      optionSelectedColor: 'rgba(255, 255, 255, 0.85)',
      optionSelectedBg: '#112322',
      optionHeight: 32,
      optionActiveBg: 'rgba(255, 255, 255, 0.08)',
      multipleSelectorBgDisabled: 'rgba(255, 255, 255, 0.08)',
      multipleItemHeightLG: 32,
      multipleItemHeight: 24,
      multipleItemColorDisabled: 'rgba(255, 255, 255, 0.25)',
      multipleItemBorderColorDisabled: 'rgba(255, 255, 255, 0)',
      multipleItemBorderColor: 'rgba(255, 255, 255, 0)',
      multipleItemBg: 'rgba(255, 255, 255, 0.12)',
      clearBg: '#131314',
      optionSelectedFontWeight: 600,
      fontFamily: 'Poppins',
      showArrowPaddingInlineEnd: 18,
      activeBorderColor: '#13a8a1',
      hoverBorderColor: '#33bcb5',
      colorErrorBg: '#2a1212',
      colorErrorBgHover: '#431414',
      colorWarningBg: '#2b1e11',
      colorWarningBgHover: '#442b11',
      colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorWarning: '#d87716',
      colorError: '#e84747',
      multipleItemHeightSM: 16,
    },
    Result: {
      paddingXS: 8,
      paddingXL: 32,
      paddingLG: 24,
      padding: 16,
      marginXS: 8,
      lineHeightHeading3: 1.3333333333333333,
      lineHeight: 1.5714285714285714,
      fontSizeHeading3: 24,
      fontSize: 14,
      colorWarning: '#d87716',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorSuccess: '#69aa19',
      colorInfo: '#13a8a1',
      colorFillAlter: 'rgba(255, 255, 255, 0.04)',
      colorError: '#e84747',
      iconFontSize: 72,
      subtitleFontSize: 14,
      titleFontSize: 24,
    },
    Progress: {
      paddingXS: 8,
      marginXXS: 4,
      marginXS: 8,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSize: 14,
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSuccess: '#69aa19',
      colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
      colorError: '#e84747',
      colorBgContainer: '#131314',
      remainingColor: 'rgba(255, 255, 255, 0.12)',
      defaultColor: '#13a8a1',
      circleTextColor: 'rgba(255, 255, 255, 0.85)',
      fontFamily: 'Poppins',
      lineBorderRadius: 100,
      colorWhite: '#ffffff',
    },
    Popconfirm: {
      marginXXS: 4,
      marginXS: 8,
      fontWeightStrong: 600,
      fontSize: 14,
      colorWarning: '#d87716',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorText: 'rgba(255, 255, 255, 0.85)',
    },
    Pagination: {
      screenSM: 576,
      screenLG: 992,
      paddingXXS: 4,
      paddingSM: 12,
      marginXXS: 4,
      marginXS: 8,
      marginSM: 12,
      margin: 16,
      lineWidthFocus: 4,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.15)',
      controlItemBgActiveDisabled: 'rgba(255, 255, 255, 0.18)',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWarningOutline: 'rgba(173, 87, 0, 0.15)',
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.25)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      colorFillAlter: 'rgba(255, 255, 255, 0.04)',
      colorErrorOutline: 'rgba(238, 38, 38, 0.11)',
      colorBorder: '#3f4042',
      colorBgTextHover: 'rgba(255, 255, 255, 0.12)',
      colorBgTextActive: 'rgba(255, 255, 255, 0.18)',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      itemSizeSM: 24,
      itemSize: 32,
      itemLinkBg: '#131314',
      itemInputBg: '#131314',
      itemBg: '#131314',
      itemActiveColorDisabled: 'rgba(255, 255, 255, 0.25)',
      itemActiveBgDisabled: 'rgba(255, 255, 255, 0.18)',
      itemActiveBg: '#131314',
      fontFamily: 'Poppins',
      itemActiveColor: '#13a8a1',
      itemActiveColorHover: '#33bcb5',
      miniOptionsSizeChangerTop: 0,
      itemSizeLG: 40,
    },
    Modal: {
      screenSMMax: 767,
      paddingXS: 8,
      paddingMD: 20,
      paddingLG: 24,
      paddingContentHorizontalLG: 24,
      padding: 16,
      marginXS: 8,
      marginSM: 12,
      marginLG: 24,
      margin: 16,
      lineWidthFocus: 4,
      lineWidth: 1,
      lineHeightHeading5: 1.5,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeLG: 16,
      fontSizeHeading5: 16,
      fontSize: 14,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      colorWarning: '#d87716',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSuccess: '#69aa19',
      colorPrimaryBorder: '#144845',
      colorIconHover: 'rgba(255, 255, 255, 0.85)',
      colorIcon: 'rgba(255, 255, 255, 0.45)',
      colorBgMask: 'rgba(12, 12, 13, 0.45)',
      titleColor: 'rgba(255, 255, 255, 0.85)',
      headerBg: '#1d1e1f',
      footerBg: 'rgba(255, 255, 255, 0)',
      contentBg: '#1d1e1f',
      titleFontSize: 16,
      titleLineHeight: 1.375,
      fontFamily: 'Poppins',
    },
    Message: {
      paddingXS: 8,
      paddingSM: 12,
      marginXS: 8,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlHeightLG: 40,
      borderRadiusLG: 8,
      colorWarning: '#d87716',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSuccess: '#69aa19',
      colorInfo: '#13a8a1',
      colorError: '#e84747',
      contentBg: '#1d1e1f',
      fontFamily: 'Poppins',
    },
    List: {
      screenSM: 576,
      screenMD: 768,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      paddingContentVerticalSM: 8,
      paddingContentVerticalLG: 16,
      paddingContentVertical: 12,
      paddingContentHorizontalLG: 24,
      paddingContentHorizontal: 16,
      padding: 16,
      marginXXS: 4,
      marginXXL: 48,
      marginSM: 12,
      marginLG: 24,
      margin: 16,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSize: 14,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusLG: 8,
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorPrimary: '#13a8a1',
      colorBorder: '#3f4042',
      headerBg: 'rgba(255, 255, 255, 0)',
      footerBg: 'rgba(255, 255, 255, 0)',
      avatarMarginRight: 16,
      descriptionFontSize: 14,
      fontFamily: 'Poppins',
    },
    FloatButton: {
      paddingXXS: 4,
      marginXXL: 48,
      marginLG: 24,
      margin: 16,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSize: 14,
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlHeightLG: 40,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      colorTextLightSolid: '#ffffff',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorPrimaryHover: '#33bcb5',
      colorPrimary: '#13a8a1',
      colorFillContent: 'rgba(255, 255, 255, 0.12)',
      colorBgElevated: '#1d1e1f',
      fontFamily: 'Poppins',
    },
    Empty: {
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      fontSize: 14,
      lineHeight: 1.5714285714285714,
    },
    Dropdown: {
      sizePopupArrow: 16,
      paddingXXS: 4,
      paddingXS: 8,
      marginXXS: 4,
      marginXS: 8,
      lineWidthFocus: 4,
      lineHeight: 1.5714285714285714,
      fontSizeSM: 12,
      fontSizeIcon: 12,
      fontSize: 14,
      controlPaddingHorizontal: 12,
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlItemBgActiveHover: '#113634',
      controlItemBgActive: '#112322',
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      colorTextLightSolid: '#ffffff',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      colorError: '#e84747',
      colorBgElevated: '#1d1e1f',
      paddingBlock: 5,
      fontFamily: 'Poppins',
    },
    Drawer: {
      paddingXS: 8,
      paddingLG: 24,
      padding: 16,
      marginSM: 12,
      lineWidth: 1,
      lineHeightLG: 1.5,
      fontWeightStrong: 600,
      fontSizeLG: 16,
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorIconHover: 'rgba(255, 255, 255, 0.85)',
      colorIcon: 'rgba(255, 255, 255, 0.45)',
      colorBgMask: 'rgba(12, 12, 13, 0.45)',
      colorBgElevated: '#1d1e1f',
      lineWidthFocus: 4,
      footerPaddingBlock: 8,
      footerPaddingInline: 16,
    },
    Divider: {
      marginXS: 8,
      marginLG: 24,
      margin: 16,
      lineWidth: 1,
      fontSizeLG: 16,
      fontSize: 14,
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      lineHeight: 1.5714285714285714,
      fontFamily: 'Poppins',
      verticalMarginInline: 8,
    },
    Descriptions: {
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      lineWidth: 1,
      lineHeightSM: 1.6666666666666667,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSize: 14,
      borderRadiusLG: 8,
      colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
      colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorFillAlter: 'rgba(255, 255, 255, 0.04)',
      titleColor: 'rgba(255, 255, 255, 0.85)',
      labelBg: 'rgba(255, 255, 255, 0.04)',
      contentColor: 'rgba(255, 255, 255, 0.85)',
      fontFamily: 'Poppins',
      labelColor: 'rgba(255, 255, 255, 0.45)',
      colonMarginLeft: 2,
      colonMarginRight: 8,
      itemPaddingBottom: 16,
      itemPaddingEnd: 16,
      titleMarginBottom: 20,
    },
    DatePicker: {
      sizePopupArrow: 16,
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      padding: 16,
      marginXXS: 4,
      marginXS: 8,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeightLG: 1.5,
      lineHeight: 1.5714285714285714,
      fontWeightStrong: 600,
      fontSizeLG: 16,
      fontSize: 14,
      controlPaddingHorizontalSM: 8,
      controlPaddingHorizontal: 12,
      controlOutlineWidth: 2,
      controlOutline: 'rgba(64, 217, 255, 0.15)',
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlItemBgActive: '#112322',
      controlHeightSM: 24,
      controlHeightLG: 40,
      controlHeight: 32,
      borderRadiusXS: 2,
      borderRadiusSM: 4,
      borderRadiusLG: 8,
      borderRadius: 6,
      colorWarningOutline: 'rgba(173, 87, 0, 0.15)',
      colorWarningHover: '#7c4915',
      colorWarning: '#d87716',
      colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.25)',
      colorTextLightSolid: '#ffffff',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorPrimaryHover: '#33bcb5',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      colorLinkHover: '#33bcb5',
      colorLinkActive: '#13857f',
      colorLink: '#13a8a1',
      colorIconHover: 'rgba(255, 255, 255, 0.85)',
      colorIcon: 'rgba(255, 255, 255, 0.45)',
      colorFillAlter: 'rgba(255, 255, 255, 0.04)',
      colorErrorOutline: 'rgba(238, 38, 38, 0.11)',
      colorErrorHover: '#791a1a',
      colorError: '#e84747',
      colorBorder: '#3f4042',
      colorBgElevated: '#1d1e1f',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      hoverBorderColor: '#33bcb5',
      cellHoverBg: 'rgba(255, 255, 255, 0.08)',
      cellHeight: 24,
      activeBorderColor: '#13a8a1',
      paddingInline: 11,
      paddingInlineSM: 7,
      colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
      colorErrorBg: '#2a1212',
      colorWarningBg: '#2b1e11',
      colorWarningText: '#d87716',
      colorErrorText: '#d32020',
      colorWarningBgHover: '#442b11',
      colorErrorBgHover: '#431414',
      activeBg: '#131314',
      hoverBg: '#131314',
      cellBgDisabled: 'rgba(255, 255, 255, 0.08)',
      cellActiveWithRangeBg: '#112322',
      cellHoverWithRangeBg: '#cbe0fd',
      cellRangeBorderColor: '#82b4f9',
      multipleItemBg: 'rgba(255, 255, 255, 0.12)',
      multipleItemBorderColor: 'rgba(255, 255, 255, 0)',
      multipleItemBorderColorDisabled: 'rgba(255, 255, 255, 0)',
      multipleItemColorDisabled: 'rgba(255, 255, 255, 0.25)',
      multipleSelectorBgDisabled: 'rgba(255, 255, 255, 0.08)',
      cellWidth: 36,
      multipleItemHeight: 24,
      multipleItemHeightLG: 32,
      multipleItemHeightSM: 16,
      paddingBlock: 4,
      paddingBlockLG: 7,
      paddingBlockSM: 0,
      paddingInlineLG: 11,
      presetsMaxWidth: 200,
      presetsWidth: 120,
      textHeight: 40,
      timeCellHeight: 28,
      timeColumnHeight: 224,
      timeColumnWidth: 56,
      withoutTimeCellHeight: 66,
      inputFontSize: 14,
      inputFontSizeLG: 16,
      inputFontSizeSM: 12,
      fontFamily: 'Poppins',
      colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
    },
    Collapse: {
      paddingXXS: 4,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      padding: 16,
      marginSM: 12,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSize: 14,
      borderRadiusLG: 8,
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorFillAlter: 'rgba(255, 255, 255, 0.04)',
      colorBorder: '#3f4042',
      colorBgContainer: '#131314',
      headerBg: 'rgba(255, 255, 255, 0.04)',
      contentBg: '#131314',
      lineHeightLG: 1.5,
      fontFamily: 'Poppins',
      borderlessContentBg: 'rgba(255, 255, 255, 0)',
    },
    Checkbox: {
      paddingXS: 8,
      marginXS: 8,
      lineWidthFocus: 4,
      lineWidthBold: 2,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      controlInteractiveSize: 16,
      borderRadiusSM: 4,
      colorWhite: '#ffffff',
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#33bcb5',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      colorBorder: '#3f4042',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      fontFamily: 'Poppins',
    },
    Breadcrumb: {
      paddingXXS: 4,
      marginXXS: 4,
      marginXS: 8,
      lineWidthFocus: 4,
      lineHeight: 1.5714285714285714,
      fontSizeIcon: 12,
      fontSize: 14,
      borderRadiusSM: 4,
      colorPrimaryBorder: '#144845',
      colorBgTextHover: 'rgba(255, 255, 255, 0.12)',
      separatorColor: 'rgba(255, 255, 255, 0.45)',
      linkHoverColor: 'rgba(255, 255, 255, 0.85)',
      linkColor: 'rgba(255, 255, 255, 0.45)',
      lastItemColor: 'rgba(255, 255, 255, 0.85)',
      itemColor: 'rgba(255, 255, 255, 0.45)',
      separatorMargin: 8,
      iconFontSize: 14,
      fontFamily: 'Poppins',
    },
    Anchor: {
      paddingXXS: 4,
      lineWidthBold: 2,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSize: 14,
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSplit: 'rgba(255, 255, 255, 0.06)',
      colorPrimary: '#13a8a1',
      linkPaddingInlineStart: 16,
      linkPaddingBlock: 4,
      fontFamily: 'Poppins',
    },
    Alert: {
      paddingMD: 20,
      paddingContentVerticalSM: 8,
      paddingContentHorizontalLG: 24,
      marginXS: 8,
      marginSM: 12,
      lineWidth: 1,
      lineHeight: 1.5714285714285714,
      fontSizeLG: 16,
      fontSizeIcon: 12,
      fontSizeHeading3: 24,
      fontSize: 14,
      borderRadiusLG: 8,
      colorWarningBorder: '#593715',
      colorWarningBg: '#2b1e11',
      colorWarning: '#d87716',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorSuccessBorder: '#324916',
      colorSuccessBg: '#1b2312',
      colorSuccess: '#69aa19',
      colorInfoBorder: '#144845',
      colorInfoBg: '#112322',
      colorInfo: '#13a8a1',
      colorIconHover: 'rgba(255, 255, 255, 0.85)',
      colorIcon: 'rgba(255, 255, 255, 0.45)',
      colorErrorBorder: '#581818',
      colorErrorBg: '#2a1212',
      colorError: '#e84747',
      withDescriptionIconSize: 24,
      fontFamily: 'Poppins',
    },
    Space: {
      paddingXS: 8,
      paddingLG: 24,
      padding: 16,
    },
    Layout: {
      bodyBg: '#000000',
      footerBg: '#000000',
      headerBg: '#001529',
      headerColor: 'rgba(255, 255, 255, 0.85)',
      lightSiderBg: '#131314',
      lightTriggerBg: '#131314',
      lightTriggerColor: 'rgba(255, 255, 255, 0.85)',
      siderBg: '#001529',
      triggerBg: '#002140',
      triggerColor: '#ffffff',
      colorText: 'rgba(255, 255, 255, 0.85)',
      headerHeight: 64,
      triggerHeight: 48,
      zeroTriggerHeight: 40,
      zeroTriggerWidth: 40,
      borderRadius: 6,
      fontSize: 14,
      fontSizeXL: 20,
    },
    Mentions: {
      fontFamily: 'Poppins',
      fontSize: 14,
      lineHeight: 1.5714285714285714,
      controlItemWidth: 100,
    },
    Splitter: {
      lineWidth: 1,
      controlHeightLG: 40,
      controlHeight: 32,
      colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      colorPrimaryBorderHover: '#14625e',
      colorPrimaryBorder: '#144845',
      colorPrimary: '#13a8a1',
      colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
      colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
      colorFillContentHover: 'rgba(255, 255, 255, 0.18)',
      colorBorderSecondary: '#2e2e30',
      colorBgElevated: '#1d1e1f',
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      colorBgContainer: '#131314',
      splitBarDraggableSize: 20,
      lineHeight: 1.5714285714285714,
      fontSize: 14,
      fontFamily: 'Poppins',
      controlHeightSM: 24,
      borderRadiusXS: 2,
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorFill: 'rgba(255, 255, 255, 0.18)',
      controlItemBgActive: '#112322',
      controlItemBgActiveHover: '#113634',
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      fontSizeSM: 12,
      splitBarSize: 2,
      splitTriggerSize: 6,
    },
  },
} satisfies ThemeConfig;
